import React from 'react';
import { Box, Typography, Paper, Chip, useTheme } from '@mui/material';
import { keyframes } from '@emotion/react';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import type { AuditStatus } from '../../types';
import {
  isCancelledStatus,
  isSuccessTerminalStatus,
  splitWorkflowStatuses,
} from '../../utils/workflowStatus';

/* ── Props ──────────────────────────────────────────────────────────── */
interface AuditProgressPipelineProps {
  statuses: AuditStatus[];
  currentStatusId: number | undefined;
  /** When provided, flow stations and cancel chips become clickable */
  onStatusChange?: (statusId: number) => void;
  /** When true, fills parent height and vertically centers content */
  fillHeight?: boolean;
  /** Compact view without card chrome, suitable for table rows */
  compact?: boolean;
}

/* ── Pulse animation for the current station ────────────────────────── */
const pulseRing = keyframes`
  0%   { transform: scale(1);   opacity: 0.5; }
  70%  { transform: scale(1.55); opacity: 0; }
  100% { transform: scale(1.55); opacity: 0; }
`;

/* ── Constants ──────────────────────────────────────────────────────── */
const CIRCLE_SIZE = 38;
const TRACK_HEIGHT = 6;

/* ── Component ──────────────────────────────────────────────────────── */
const AuditProgressPipeline: React.FC<AuditProgressPipelineProps> = ({
  statuses,
  currentStatusId,
  onStatusChange,
  fillHeight = false,
  compact = false,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const { flowStatuses, cancelledStatuses } = splitWorkflowStatuses(statuses);
  const n = flowStatuses.length;
  if (n === 0 && cancelledStatuses.length === 0) return null;

  const currentStatus = statuses.find((status) => status.id === currentStatusId);
  const currentName = currentStatus?.name ?? 'Nicht festgelegt';
  const isCancelled = isCancelledStatus(currentName);
  const isSuccessTerminal = isSuccessTerminalStatus(currentName);
  const flowCurrentIndex = isCancelled
    ? -1
    : flowStatuses.findIndex((status) => status.id === currentStatusId);

  /* ── Colours ─────────────────────────────────────────────────────── */
  const successAccent = isDark ? '#3dd47e' : '#2ba862';
  const cancelledAccent = isDark ? '#ef5350' : '#d32f2f';
  const accent = isCancelled ? cancelledAccent : successAccent;
  const accentFaded = isCancelled
    ? (isDark ? 'rgba(239,83,80,0.15)' : 'rgba(211,47,47,0.10)')
    : (isDark ? 'rgba(61,212,126,0.15)' : 'rgba(43,168,98,0.10)');
  const reachedBorder = isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.22)';
  const reachedBg = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';
  const trackBg = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)';
  const futureBorder = isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)';
  const futureBg = isDark ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.8)';

  const tubeTop = (CIRCLE_SIZE - TRACK_HEIGHT) / 2;
  const fillWidthPct =
    flowCurrentIndex <= 0 || n <= 1 ? 0 : (flowCurrentIndex / (n - 1)) * 100;

  const showCancelledTerminal =
    cancelledStatuses.length > 0 && (isCancelled || Boolean(onStatusChange));

  return (
    <Paper
      elevation={0}
      sx={{
        px: compact ? 0 : 3,
        pt: compact ? 0 : (fillHeight ? 1.5 : 2.5),
        pb: compact ? 0 : (fillHeight ? 1.5 : 3),
        mb: compact ? 0 : 2,
        borderRadius: compact ? 0 : 3,
        bgcolor: compact ? 'transparent' : 'background.paper',
        border: compact ? 'none' : `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
        boxShadow: compact ? 'none' : undefined,
        ...(fillHeight && {
          height: '100%',
          marginBottom: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          minHeight: 0,
        }),
      }}
    >
      {!compact && (
        <>
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{
              fontWeight: 600,
              fontSize: '0.6rem',
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              display: 'block',
              mb: 0.25,
            }}
          >
            Audit-Fortschritt
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: fillHeight ? 1.25 : 3, fontSize: '0.82rem' }}
          >
            Aktuelle Phase:{' '}
            <Box component="span" sx={{ color: accent, fontWeight: 600 }}>
              {currentName}
            </Box>
          </Typography>
        </>
      )}

      <Box
        sx={{
          ...(fillHeight && {
            flex: 1,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }),
        }}
      >
        {n > 0 && (
          <Box sx={{ position: 'relative', width: '100%' }}>
            <Box
              sx={{
                position: 'absolute',
                top: tubeTop,
                left: 0,
                width: '100%',
                height: TRACK_HEIGHT,
                borderRadius: TRACK_HEIGHT / 2,
                bgcolor: trackBg,
              }}
            />

            {fillWidthPct > 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  top: tubeTop,
                  left: 0,
                  width: `${fillWidthPct}%`,
                  height: TRACK_HEIGHT,
                  borderRadius: TRACK_HEIGHT / 2,
                  background: `linear-gradient(90deg, ${isCancelled ? reachedBorder : successAccent}, ${isCancelled ? `${reachedBorder}cc` : `${successAccent}cc`})`,
                  transition: 'width 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              />
            )}

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                position: 'relative',
                zIndex: 1,
              }}
            >
              {flowStatuses.map((status, index) => {
                const isCompleted = !isCancelled && flowCurrentIndex >= 0 && index < flowCurrentIndex;
                const isCurrent = !isCancelled && index === flowCurrentIndex;
                const isFuture = isCancelled || flowCurrentIndex < 0 || index > flowCurrentIndex;
                const isReachedBeforeCancel = isCancelled && !isSuccessTerminal;

                return (
                  <Box
                    key={status.id}
                    sx={{
                      flex: '0 0 auto',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                    }}
                  >
                    <Box
                      onClick={onStatusChange ? () => onStatusChange(status.id) : undefined}
                      sx={{
                        position: 'relative',
                        width: CIRCLE_SIZE,
                        height: CIRCLE_SIZE,
                        ...(onStatusChange && {
                          cursor: 'pointer',
                          '&:hover > :last-child': {
                            transform: 'scale(1.12)',
                            boxShadow: `0 0 16px ${successAccent}44`,
                          },
                        }),
                      }}
                    >
                      {isCurrent && (
                        <Box
                          sx={{
                            position: 'absolute',
                            inset: 0,
                            borderRadius: '50%',
                            border: `2px solid ${successAccent}`,
                            animation: `${pulseRing} 2s ease-out infinite`,
                          }}
                        />
                      )}

                      <Box
                        sx={{
                          width: '100%',
                          height: '100%',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.4s ease',
                          ...(isCompleted && {
                            bgcolor: successAccent,
                            border: `2px solid ${successAccent}`,
                          }),
                          ...(isReachedBeforeCancel && {
                            bgcolor: reachedBg,
                            border: `2px solid ${reachedBorder}`,
                          }),
                          ...(isCurrent && {
                            bgcolor: successAccent,
                            border: `2px solid ${successAccent}`,
                            boxShadow: `0 0 14px ${successAccent}55`,
                          }),
                          ...(isFuture && !isReachedBeforeCancel && {
                            bgcolor: futureBg,
                            border: `2px dashed ${futureBorder}`,
                          }),
                        }}
                      >
                        {(isCompleted || isReachedBeforeCancel) && (
                          <CheckIcon
                            sx={{
                              fontSize: 18,
                              color: isReachedBeforeCancel ? reachedBorder : '#fff',
                            }}
                          />
                        )}
                        {isCurrent && (
                          <CheckIcon sx={{ fontSize: 20, color: '#fff' }} />
                        )}
                        {isFuture && !isReachedBeforeCancel && !isCurrent && (
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: futureBorder,
                            }}
                          />
                        )}
                      </Box>
                    </Box>

                    <Typography
                      variant="caption"
                      sx={{
                        mt: 1,
                        fontWeight: isCurrent ? 700 : isCompleted || isReachedBeforeCancel ? 600 : 400,
                        fontSize: '0.68rem',
                        color: isCurrent
                          ? successAccent
                          : isCompleted || isReachedBeforeCancel
                            ? 'text.primary'
                            : 'text.disabled',
                        textAlign: 'center',
                        lineHeight: 1.2,
                        maxWidth: 100,
                        ...(isCurrent && {
                          bgcolor: accentFaded,
                          px: 1,
                          py: 0.25,
                          borderRadius: 1,
                        }),
                      }}
                    >
                      {status.name}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}

        {showCancelledTerminal && (
          <Box
            sx={{
              mt: compact ? 0.75 : 1.5,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            {!compact && onStatusChange && (
              <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.62rem' }}>
                Abschluss (direkt setzen)
              </Typography>
            )}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, justifyContent: 'center' }}>
              {cancelledStatuses.map((status) => {
                const isActive = currentStatusId === status.id;
                return (
                  <Chip
                    key={status.id}
                    size="small"
                    icon={<CloseIcon sx={{ fontSize: '14px !important' }} />}
                    label={status.name}
                    color={isActive ? 'error' : 'default'}
                    variant={isActive ? 'filled' : 'outlined'}
                    clickable={Boolean(onStatusChange)}
                    onClick={onStatusChange ? () => onStatusChange(status.id) : undefined}
                    sx={{
                      fontSize: '0.68rem',
                      ...(onStatusChange && !isActive && {
                        borderColor: cancelledAccent,
                        color: cancelledAccent,
                      }),
                    }}
                  />
                );
              })}
            </Box>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default AuditProgressPipeline;
